<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="duel-pick"
      :style="theme.cssVars"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      @pointerup.self="$emit('cancel')"
    >
      <div
        class="duel-pick-panel"
        @pointerdown.stop
        @pointerup.stop
      >
        <h2 :id="titleId">{{ t('nav.duelPick') }}</h2>
        <button type="button" class="choice fill" autofocus @click="$emit('ai')">
          <span class="choice-title">{{ t('nav.duelVsAi') }}</span>
          <span class="choice-hint">{{ t('nav.duelVsAiHint') }}</span>
        </button>
        <button
          type="button"
          class="choice"
          :disabled="ranked.closed"
          @click="$emit('ranked')"
        >
          <span class="choice-title">{{ t('nav.duelRanked') }}</span>
          <span v-if="ranked.closed" class="choice-hint">{{ t('nav.queueClosed') }}</span>
          <span v-else-if="ranked.waiting !== null" class="choice-hint queue">
            {{ t('nav.queueWaiting', { n: ranked.waiting }) }}
          </span>
          <span v-if="!ranked.closed && duelPrefs.rankedPrompts && nudgeCoins > 0 && ranked.offerPending > 0" class="choice-hint offer">
            {{ t('nav.rankedOfferHint', { n: nudgeCoins }) }}
          </span>
          <span v-else-if="!ranked.closed" class="choice-hint">{{ t('nav.duelRankedHint') }}</span>
        </button>
        <button
          type="button"
          class="choice"
          :disabled="ranked.closed"
          @click="$emit('casual')"
        >
          <span class="choice-title">{{ t('nav.duelCasual') }}</span>
          <span v-if="ranked.closed" class="choice-hint">{{ t('nav.queueClosed') }}</span>
          <span v-else-if="ranked.casualWaiting !== null" class="choice-hint queue">
            {{ t('nav.queueWaiting', { n: ranked.casualWaiting }) }}
          </span>
          <span v-if="!ranked.closed" class="choice-hint">
            {{ t('nav.duelCasualHint') }}
          </span>
        </button>
        <button
          v-if="site.eventQueue"
          type="button"
          class="choice"
          @click="$emit('event')"
        >
          <span class="choice-title">{{ t(`eventQueue.modes.${site.eventQueue.mode}`) }}</span>
          <span v-if="ranked.eventWaiting !== null" class="choice-hint queue">
            {{ t('nav.queueWaiting', { n: ranked.eventWaiting }) }}
          </span>
          <span v-if="site.eventCountdown" class="choice-hint">
            {{ t('eventQueue.endsIn', { time: site.eventCountdown }) }}
          </span>
          <span class="choice-hint">{{ t('nav.duelEventHint') }}</span>
        </button>
        <button type="button" class="cancel" @click="$emit('cancel')">
          {{ t('nav.duelCancel') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from '../i18n/t'
import { useThemeStore } from '../stores/theme'
import { useRankedLobbyStore } from '../stores/rankedLobby'
import { useSiteStore } from '../stores/site'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { RANKED_NUDGE_COINS } from '@shared/index'

defineProps<{ visible: boolean }>()
defineEmits<{ cancel: []; ai: []; ranked: []; casual: []; event: [] }>()

const { t } = useI18n()
const theme = useThemeStore()
const ranked = useRankedLobbyStore()
const site = useSiteStore()
const duelPrefs = useDuelPrefsStore()
const nudgeCoins = RANKED_NUDGE_COINS
const titleId = 'duel-mode-pick-title'
</script>

<style scoped>
.duel-pick {
  position: fixed;
  inset: 0;
  z-index: 400;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  background: var(--md-scrim);
  font-family: Roboto, system-ui, sans-serif;
}

.duel-pick-panel {
  width: min(420px, 100%);
  margin-top: auto;
  margin-bottom: auto;
  flex: 0 0 auto;
  padding: 24px 20px 12px;
  border-radius: 16px;
  background-color: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  border: 1px solid var(--md-sys-color-outline-variant);
  box-shadow: var(--md-elevation-2);
}

h2 {
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 500;
  text-align: center;
}

.choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  margin: 0 0 8px;
  padding: 14px 16px;
  border: 1px solid var(--md-sys-color-outline);
  border-radius: 12px;
  background-color: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.choice.fill {
  border-color: transparent;
  background-color: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.choice:hover {
  background-color: var(--md-sys-color-surface);
}

.choice.fill:hover {
  background-color: var(--md-sys-color-primary);
  filter: brightness(1.06);
}

.choice-title {
  font-size: 16px;
  font-weight: 700;
}

.choice-hint {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.35;
  opacity: 0.85;
}

.choice-hint.queue {
  font-weight: 700;
  opacity: 1;
}

.choice-hint.offer {
  color: var(--md-sys-color-primary);
  font-weight: 700;
  opacity: 1;
}

.choice:disabled {
  opacity: 0.55;
  cursor: default;
}

.choice:focus-visible,
.cancel:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

.cancel {
  display: block;
  width: 100%;
  min-height: 44px;
  margin-top: 4px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--md-sys-color-primary);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

@media (orientation: landscape) and (max-height: 520px) {
  .duel-pick {
    padding: max(8px, env(safe-area-inset-top))
      max(12px, env(safe-area-inset-right))
      max(8px, env(safe-area-inset-bottom))
      max(12px, env(safe-area-inset-left));
  }

  .duel-pick-panel {
    padding: 12px 14px 4px;
  }

  h2 {
    margin-bottom: 8px;
    font-size: 18px;
  }

  .choice {
    gap: 0;
    margin-bottom: 6px;
    padding: 8px 12px;
  }

  .choice-title {
    font-size: 15px;
  }

  .choice-hint {
    font-size: 12px;
    line-height: 1.25;
  }

  .cancel {
    min-height: 36px;
    margin-top: 0;
  }
}
</style>
